import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { getStoreSettings } from "../firebase/firestoreService";
import "../styles/navbar.css";

const LAST_SEEN_KEY = "gvs_notif_last_seen";

function BagIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 01-8 0" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export default function Navbar({ cartCount, onCartClick }) {
  const { user, openSignIn, isAdmin, isDeliveryStaff, isEventStaff } = useAuth();
  const navigate  = useNavigate();
  const prevCount = useRef(cartCount);
  const [bouncing, setBouncing] = useState(false);

  useEffect(() => {
    if (cartCount > prevCount.current) {
      setBouncing(true);
      const t = setTimeout(() => setBouncing(false), 500);
      prevCount.current = cartCount;
      return () => clearTimeout(t);
    }
    prevCount.current = cartCount;
  }, [cartCount]);

  const [notifCount,    setNotifCount]    = useState(0);
  const [eventsEnabled, setEventsEnabled] = useState(false);

  useEffect(() => {
    getStoreSettings().then((s) => setEventsEnabled(!!s.eventsEnabled));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const lastSeen     = localStorage.getItem(LAST_SEEN_KEY);
    const lastSeenDate = lastSeen ? new Date(lastSeen) : new Date(0);
    const isNew = (data) => {
      const ts = data.createdAt;
      if (!ts) return false;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d > lastSeenDate;
    };
    let orderNew = 0;
    let userNew  = 0;
    const unsubOrders = db.collection("orders").orderBy("createdAt", "desc").limit(50)
      .onSnapshot((snap) => { orderNew = snap.docs.filter((d) => isNew(d.data())).length; setNotifCount(orderNew + userNew); });
    const unsubUsers  = db.collection("users").orderBy("createdAt", "desc").limit(50)
      .onSnapshot((snap) => { userNew  = snap.docs.filter((d) => isNew(d.data())).length; setNotifCount(orderNew + userNew); });
    return () => { unsubOrders(); unsubUsers(); };
  }, [isAdmin]);

  const displayName = user
    ? (user.displayName?.split(" ")[0] || user.phoneNumber || "Account")
    : null;

  return (
    <nav className="navbar">
      <div className="navbar__inner">
        <span className="navbar__logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          GOOD <em>vibes</em> STORE
        </span>

        <div className="navbar__right">
          {isAdmin && (
            <>
              <button className="navbar__admin-btn" onClick={() => navigate("/admin")}>
                Admin
              </button>
              <button className="navbar__bell-btn" onClick={() => navigate("/admin?tab=notifications")}>
                <BellIcon />
                {notifCount > 0 && (
                  <span className="navbar__bell-badge">{notifCount > 99 ? "99+" : notifCount}</span>
                )}
              </button>
            </>
          )}
          {!isAdmin && isDeliveryStaff && (
            <button className="navbar__admin-btn" onClick={() => navigate("/delivery")}>
              Orders
            </button>
          )}
          {!isAdmin && isEventStaff && (
            <button className="navbar__admin-btn" onClick={() => navigate("/scan")}>
              Scan
            </button>
          )}
          {user ? (
            <button className="navbar__user-name" onClick={() => navigate("/profile")}>
              <UserIcon />
              <span>Hi, {displayName}</span>
            </button>
          ) : (
            <button className="navbar__signin-btn" onClick={() => openSignIn()}>
              Sign in
            </button>
          )}

          <button className={`navbar__cart-btn${bouncing ? " navbar__cart-btn--bounce" : ""}`} onClick={onCartClick}>
            <BagIcon />
            {cartCount > 0 && <span className="navbar__badge">{cartCount}</span>}
          </button>
        </div>
      </div>

      {(eventsEnabled || isAdmin) && (
        <div className="navbar__subrow">
          <button className="navbar__events-btn" onClick={() => navigate("/events")}>
            Events{!eventsEnabled && isAdmin ? " (dev only)" : ""}
          </button>
        </div>
      )}
    </nav>
  );
}
